import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import './TeacherDashboard.css'; // We'll create this CSS file
import logo from "../../assets/navLogo.svg";

const TeacherDashboard = () => {
    const [assignedTests, setAssignedTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [score, setScore] = useState('');
    const [feedback, setFeedback] = useState('');
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [debugInfo, setDebugInfo] = useState('');
    const [activeTab, setActiveTab] = useState('grading');
    const [stats, setStats] = useState({
        totalAssigned: 0,
        graded: 0,
        pending: 0
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchAssignedTests();
        checkDebugInfo();
    }, []);

    useEffect(() => {
        calculateStats();
    }, [assignedTests]);

    const calculateStats = () => {
        const total = assignedTests.length;
        const graded = assignedTests.filter(test => test.score !== null).length;
        const pending = total - graded;

        setStats({
            totalAssigned: total,
            graded,
            pending
        });
    };

    const fetchAssignedTests = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            console.log('Fetching tests for teacher:', user);

            if (!user || !user.id) {
                setMessage('No user ID found. Please login again.');
                setLoading(false);
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/teacher/assigned-tests/${user.id}`
            );
            console.log('Assigned tests response:', response.data);
            setAssignedTests(response.data);
        } catch (error) {
            console.error('Error fetching assigned tests:', error);
            setMessage('Error loading assigned tests: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const checkDebugInfo = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/debug/teacher-assignments`
            );
            setDebugInfo(`Total assignments in system: ${response.data.length}`);
        } catch (error) {
            console.error('Error fetching debug info:', error);
        }
    };

    const gradeTest = async (assignmentId) => {
        if (!score || score < 0 || score > 10) {
            setMessage('Please enter a valid score between 0 and 10');
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/teacher/grade-writing-test`, {
                assignment_id: assignmentId,
                score: parseFloat(score),
                feedback: feedback
            });

            setMessage('Test graded successfully!');
            setSelectedTest(null);
            setScore('');
            setFeedback('');
            fetchAssignedTests();
        } catch (error) {
            console.error('Error grading test:', error);
            setMessage('Error grading test: ' + (error.response?.data?.message || error.message));
        }
    };

    const parseWritingResponse = (responses) => {
        if (typeof responses === 'string') {
            try {
                const parsed = JSON.parse(responses);
                return parsed.writing || responses;
            } catch {
                return responses;
            }
        }
        return responses?.writing || 'No response';
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const getGradeColor = (score) => {
        if (score >= 8.5) return 'text-green-600';
        if (score >= 7) return 'text-blue-600';
        if (score >= 5) return 'text-yellow-600';
        return 'text-red-600';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading teacher assignments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Teacher Navbar */}
            <nav className="bg-white shadow-lg border-b border-blue-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="navbar-brand">
                            <Link to="/teacher/dashboard">
                                <img src={logo} alt="Logo" className="responsive-logo" />
                            </Link>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-600">
                                Welcome, Teacher!
                            </div>
                            <button
                                onClick={handleLogout}
                                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition duration-200 font-semibold"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Teacher Dashboard</h1>
                    <p className="text-gray-600">Manage and grade student writing assignments</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                        <div className="flex items-center">
                            <div className="p-3 bg-blue-100 rounded-lg mr-4">
                                <span className="text-blue-600 text-xl">📝</span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Total Assigned</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.totalAssigned}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                        <div className="flex items-center">
                            <div className="p-3 bg-green-100 rounded-lg mr-4">
                                <span className="text-green-600 text-xl">✅</span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Graded</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.graded}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                        <div className="flex items-center">
                            <div className="p-3 bg-orange-100 rounded-lg mr-4">
                                <span className="text-orange-600 text-xl">⏳</span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Pending Review</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Debug Info */}
                {debugInfo && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center">
                            <span className="text-yellow-600 mr-2">🔍</span>
                            <span className="text-yellow-800 text-sm">{debugInfo}</span>
                        </div>
                    </div>
                )}

                {/* Message Alert */}
                {message && (
                    <div className={`mb-6 p-4 rounded-lg ${message.includes('success')
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        <div className="flex items-center">
                            <span className="mr-2">{message.includes('success') ? '✅' : '⚠️'}</span>
                            {message}
                        </div>
                    </div>
                )}

                {/* Main Content Area */}
                {selectedTest ? (
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Grade Writing Test</h2>
                            <button
                                onClick={() => setSelectedTest(null)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Student Info */}
                        <div className="bg-blue-50 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <p className="text-sm text-blue-600 font-semibold">Student</p>
                                    <p className="text-lg">{selectedTest.student_first_name} {selectedTest.student_last_name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600 font-semibold">Grade Level</p>
                                    <p className="text-lg">{selectedTest.student_grade}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600 font-semibold">Assigned By</p>
                                    <p className="text-lg">{selectedTest.assigned_by_first_name} {selectedTest.assigned_by_last_name}</p>
                                </div>
                            </div>
                        </div>

                        {/* Writing Response */}
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3 text-gray-900">Writing Response</h3>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                                    {parseWritingResponse(selectedTest.responses)}
                                </p>
                            </div>
                        </div>

                        {/* AI Feedback */}
                        {selectedTest.ai_feedback && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-3 text-gray-900">AI Feedback</h3>
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                    <p className="whitespace-pre-wrap text-purple-800 leading-relaxed">
                                        {selectedTest.ai_feedback}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Grading Form */}
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Score (0-10)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        step="0.1"
                                        value={score}
                                        onChange={(e) => setScore(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                        placeholder="Enter score (e.g., 8.5)"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800">
                                        ⏳ Pending Review
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Teacher Feedback
                                </label>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    rows="6"
                                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                    placeholder="Provide constructive feedback for the student. Focus on strengths and areas for improvement..."
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => gradeTest(selectedTest.id)}
                                    className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition duration-200 flex items-center"
                                >
                                    <span className="mr-2">✓</span>
                                    Submit Grade
                                </button>
                                <button
                                    onClick={() => setSelectedTest(null)}
                                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Assigned Writing Tests</h2>
                            <button
                                onClick={fetchAssignedTests}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition duration-200 flex items-center"
                            >
                              
                                Refresh
                            </button>
                        </div>

                        {assignedTests.length === 0 ? (
                            <div className="text-center py-12">
                                
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Tests Assigned</h3>
                                <p className="text-gray-500 max-w-md mx-auto">
                                    Writing tests will appear here when they are assigned by an administrator.
                                    Check back later for new assignments.
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {assignedTests.map(test => (
                                    <div key={test.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition duration-200">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center mb-3">
                                                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                                                        {test.student_first_name?.[0]}{test.student_last_name?.[0]}
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {test.student_first_name} {test.student_last_name}
                                                        </h3>
                                                        <p className="text-gray-600">Grade {test.student_grade}</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                                                    <div>
                                                        <span className="font-semibold">Assigned:</span>{' '}
                                                        {new Date(test.assigned_at).toLocaleDateString()}
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold">By:</span>{' '}
                                                        {test.assigned_by_first_name} {test.assigned_by_last_name}
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold">Taken:</span>{' '}
                                                        {new Date(test.test_taken_at).toLocaleDateString()}
                                                    </div>
                                                </div>

                                                {test.score !== null && (
                                                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                                                        <span className="mr-1">✓</span>
                                                        Graded: <span className={`font-bold ml-1 ${getGradeColor(test.score)}`}>{test.score}/10</span>
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => setSelectedTest(test)}
                                                className={`ml-4 px-6 py-3 rounded-lg font-semibold transition duration-200 ${test.score !== null
                                                        ? 'bg-green-500 hover:bg-green-600 text-white'
                                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                                    }`}
                                            >
                                                {test.score !== null ? 'Review Grade' : 'Grade Test'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeacherDashboard;